import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useBackgroundAudio from '../hooks/useBackgroundAudio';

/**
 * TRANSLATE Interactive - A contemplative choice-based experience
 * 
 * Pre-written boundary statements appear as choices.
 * User selects one (or silence). Others fade.
 * Then proceeds to anchor and close.
 */

const STATEMENTS = [
  'I am not available for this.',
  'This does not work for me.',
  'I will not continue.',
  'My capacity ends here.',
  'I am stepping back.'
];

const TranslateInteractive = ({ onComplete }) => {
  const [phase, setPhase] = useState(0);
  // Phase 0: Opening microcopy "If words are needed..."
  // Phase 1: "Choose one." + statements appear
  // Phase 2: Selection made, others fade
  // Phase 3: Pause
  // Phase 4: "What is clear remains clear."
  // Phase 5: Exit to anchor/close

  const [showOpeningText, setShowOpeningText] = useState(false);
  const [showChooseText, setShowChooseText] = useState(false);
  const [showStatements, setShowStatements] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null); // null = none, -1 = silence
  const [showSealText, setShowSealText] = useState(false);
  
  const activeTimersRef = useRef([]);

  // Subtle background audio
  useBackgroundAudio(true);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      activeTimersRef.current.forEach(t => clearTimeout(t));
    };
  }, []);

  // Phase 0: Opening microcopy
  useEffect(() => {
    if (phase === 0) {
      const showTimer = setTimeout(() => {
        setShowOpeningText(true);
      }, 1000);
      
      const nextTimer = setTimeout(() => {
        setShowOpeningText(false);
        setPhase(1);
      }, 4500);
      
      activeTimersRef.current.push(showTimer, nextTimer);
    }
  }, [phase]);

  // Phase 1: Show "Choose one." and statements
  useEffect(() => {
    if (phase === 1) {
      const chooseTimer = setTimeout(() => {
        setShowChooseText(true);
      }, 500);
      
      const statementsTimer = setTimeout(() => {
        setShowStatements(true);
      }, 2000);
      
      activeTimersRef.current.push(chooseTimer, statementsTimer);
    }
  }, [phase]);

  // Handle statement selection
  const handleSelect = useCallback((index) => {
    if (selectedIndex !== null) return; // Already selected
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    
    setSelectedIndex(index);
    setShowChooseText(false);
    setPhase(2);
    
    // Phase 2 -> 3: Pause after selection
    const pauseTimer = setTimeout(() => {
      setPhase(3);
    }, 3000);
    
    // Phase 3 -> 4: Show seal text
    const sealTimer = setTimeout(() => {
      setShowSealText(true);
      setPhase(4);
    }, 4500);
    
    // Phase 4 -> 5: Complete
    const completeTimer = setTimeout(() => {
      setShowSealText(false);
      setPhase(5);
      setTimeout(() => onComplete(), 1500);
    }, 7500);
    
    activeTimersRef.current.push(pauseTimer, sealTimer, completeTimer);
  }, [selectedIndex, onComplete]);

  // Handle silence selection
  const handleSilence = useCallback(() => {
    handleSelect(-1); // -1 represents silence
  }, [handleSelect]);

  return (
    <div 
      className="fixed inset-0 z-50 overflow-hidden flex flex-col items-center justify-center"
      style={{ backgroundColor: 'hsl(240, 8%, 3%)' }}
    >
      {/* Phase 0: Opening microcopy */}
      <AnimatePresence>
        {showOpeningText && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute font-display text-base sm:text-lg text-center italic px-6"
            style={{ color: 'hsl(40, 10%, 50%)' }}
          >
            If words are needed, let them be few.
          </motion.p>
        )}
      </AnimatePresence>

      {/* Phase 1: "Choose one." - Header at top */}
      <AnimatePresence>
        {showChooseText && (
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.55 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute top-[12%] sm:top-[15%] font-display text-lg sm:text-xl tracking-[0.15em] text-center"
            style={{ color: 'hsl(40, 10%, 55%)' }}
          >
            Choose one.
          </motion.h2>
        )}
      </AnimatePresence>

      {/* Statements */}
      <AnimatePresence>
        {showStatements && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center gap-6 sm:gap-8 px-6"
          >
            {STATEMENTS.map((statement, index) => {
              const isSelected = selectedIndex === index;
              const isFading = selectedIndex !== null && selectedIndex !== index;
              
              return (
                <motion.button
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ 
                    opacity: isFading ? 0 : (isSelected ? 0.7 : 0.55),
                    y: 0,
                    scale: isSelected ? 1.02 : 1
                  }}
                  transition={{ 
                    delay: selectedIndex === null ? index * 0.15 : 0,
                    duration: isFading ? 2 : 1.5 
                  }}
                  onClick={() => handleSelect(index)}
                  disabled={selectedIndex !== null}
                  className="font-display text-base sm:text-lg tracking-wide text-center transition-all duration-700 hover:opacity-70 disabled:cursor-default"
                  style={{ 
                    color: isSelected ? 'hsl(40, 12%, 58%)' : 'hsl(40, 10%, 50%)',
                    background: 'transparent',
                    border: 'none'
                  }}
                >
                  {statement}
                </motion.button>
              );
            })}

            {/* Silence option */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ 
                opacity: selectedIndex === -1 ? 0.6 : (selectedIndex !== null ? 0 : 0.35),
                y: 0 
              }}
              transition={{ 
                delay: selectedIndex === null ? STATEMENTS.length * 0.15 + 0.3 : 0,
                duration: selectedIndex !== null && selectedIndex !== -1 ? 2 : 1.5 
              }}
              onClick={handleSilence}
              disabled={selectedIndex !== null}
              className="font-display text-sm sm:text-base tracking-wide text-center italic mt-4 transition-all duration-700 hover:opacity-50 disabled:cursor-default"
              style={{ 
                color: 'hsl(40, 8%, 40%)',
                background: 'transparent',
                border: 'none'
              }}
            >
              (or choose silence)
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phase 4: Seal text */}
      <AnimatePresence>
        {showSealText && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.75 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute font-display text-base sm:text-lg text-center"
            style={{ color: 'hsl(40, 12%, 70%)' }}
          >
            What is clear remains clear.
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TranslateInteractive;
